import {
  Component,
  AfterViewInit,
  ViewEncapsulation,
  input,
  inject,
  output,
  effect,
} from '@angular/core';
import { TreeNode } from '@ali-hm/angular-tree-component';
import { AuthService } from '../../../../authentication/auth.service';
import { CommonModule } from '@angular/common';
import {
  generateColorFromString,
  generateHistoryRecordText,
  getPrefix,
} from '../utils/comman';
import { MatExpansionModule } from '@angular/material/expansion';
import { MaterialModule } from '../../../../modules/material.module';
import { MatChipsModule } from '@angular/material/chips';
import { HighlightTextPipe } from '../../../../shared/pipe/highlight-text.pipe';
import {
  HISTORY_HIGHLIGHT_TYPES,
  APP_CONFIG,
  PRIORITY_LIST,
  STATUS_LIST,
} from '../utils/constant';
import { TextLimitPipe } from '../../../../shared/pipe/text-limit.pipe';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { TreeItemService } from '../../../../shared/services/tree-item.service';
import { TreeEventType } from '../../../../shared/utils/enums';
import { Attachment } from '../../../../shared/utils/types';
import { ThumbnailComponent } from "../../../../shared/components/thumbnail/thumbnail.component";
import { DateFormatPipe } from '../../../../shared/pipe/date-format.pipe';

@Component({
  selector: 'app-item',
  templateUrl: './item.component.html',
  styleUrls: ['./item.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MaterialModule,
    MatChipsModule,
    HighlightTextPipe,
    TextLimitPipe,
    DateFormatPipe,
    ThumbnailComponent
],
  animations: [
    trigger('toggleContainer', [
      state(
        'expand',
        style({
          height: '*', // Use '*' to allow the height to expand to its content
          opacity: 1,
          overflow: 'hidden',
          display: 'block',
        })
      ),
      state(
        'collapse',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
          display: 'none',
        })
      ),
      transition('expand <=> collapse', animate('0.5s ease-in-out')),
    ]),
  ],
})
export class ItemComponent implements AfterViewInit {
  node = input<TreeNode>(); // data for this tree node
  private auth = inject(AuthService);
  private treeItemService = inject(TreeItemService);

  expandItemDetails = output<{
    itemId: string;
    isExpanded: boolean;
    isHistoryExpanded: boolean;
  }>();
  isExpanded = input<boolean>(false);
  isHistoryExpanded = input<boolean>(false);
  itemStatusString = '';
  itemPriorityString = '';
  attachments: Attachment[] = [];

  types = HISTORY_HIGHLIGHT_TYPES.map((type) => type.name);

  status = STATUS_LIST;

  priority = PRIORITY_LIST;
  readonly NO_OF_EVENTS_IN_RECENT_HISTORY = APP_CONFIG.NO_OF_EVENTS_IN_RECENT_HISTORY;

  constructor() {
    effect (() => {
      this.itemStatusString = this.node().data.status.toLowerCase();
      this.itemPriorityString = this.node().data.priority.toLowerCase();
      this.attachments = [...this.node().data?.attachments || []];
    })
  }

  ngAfterViewInit(): void {
    // use regex on item status and priority string values to apply category labels to the item status and priority header elements in the form of
    this.itemStatusString = this.node().data.status.toLowerCase();
    this.itemPriorityString = this.node().data.priority.toLowerCase();

    // modify the item color to be a function of its type (a string)
    const itemElement = document.getElementById('item_' + this.node().data.id);
    const itemType = this.node().data.type.toLowerCase();
    const itemColor = this.generateColorFromString(itemType);
    if (itemElement) {
      itemElement.style.backgroundColor = itemColor;
    }

    // tag items for which initial tutorial popover should be shown
    const itemTitleString = this.node().data.title.toLowerCase();
    if (itemTitleString == 'my first project' && itemElement) {
      itemElement.classList.add('my-first-project-item');
    }
  }

  /**
   * a string hsl representation of a color based on a hardwired mapping function
   */
  generateColorFromString(str: string): string {
    return generateColorFromString(str);
  }

  /**
   * this item is shared with any other users in addition to the current user
   */
  isShared(): boolean {
    return this.node().data['shared_with'].length > 1;
  }

  /**
   * zoom to display just this item and its descendents
   */
  zoomToThisItem(): void {
    window.location.hash = `#/${this.node().data.id}`;
  }

  /**
   * list of item subscriber and assignees that should be displayed on the frontend to indicate the subscribers for this item e.g., given ["currentUser@gmail.com", "user2@gmail.com", "user3@gmail.com"], return 'Me, user2, user3'
   */
  getDisplayText(itemsArray: any[]): string {
    let fullDisplayText = '';
    itemsArray.map((item, index) => {
      fullDisplayText = `${fullDisplayText}${this.getUserDisplayTextForPermissions(
        item
      )}${index < itemsArray.length - 1 ? ', ' : ''}`;
    });
    return fullDisplayText;
  }

  /**
   * given a list of this item's strictly inherited permissions and its explicit permissions, returns returns a string of the text that should be displayed on the frontend to indicate the users this item is shared with (not including the current user)
   */
  getPermissionsDisplayText(allPermissions: any[]): string {
    let fullDisplayText = '';
    let isFirstPermission = true;

    for (const permission of allPermissions) {
      fullDisplayText = `${fullDisplayText}${
        !isFirstPermission ? ', ' : ''
      }${this.getUserDisplayTextForPermissions(permission.user.email)}`;
      isFirstPermission = false;
    }

    if (!fullDisplayText) {
      fullDisplayText = '(none)';
    }

    return fullDisplayText;
  }

  /**
   * given a user email corresponding to a user with whom an item is shared (as a string), returns the text of how this user is referenced and displayed in the frontend (as a string)  - basically, if the email corresponds to the current user, return "Me" else return just the prefix of the email to be displayed
   */
  private getUserDisplayTextForPermissions(userEmail: string): string {
    const currentUser = this.auth.getCurrentUser()?.email;
    return userEmail == currentUser ? 'Me' : getPrefix(userEmail);
  }

  /**
   * if it has children; otherwise, return false
   */
  isChildrenExist(node: TreeNode): boolean {
    return node && node.children?.length > 0;
  }

  /**
   * moves this item and its descendents from trash back to the main dashboard; puts it back in the same position within the original tree
   */
  moveFromTrashButtonAction(): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.MoveFromTrash,
      data: this.node().data.id,
    });
  }

  /**
   * add a child item for this item
   */
  addChildItem(): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.AddChildItem,
      data: this.node().data.id,
    });
  }

  /**
   * given an item history record object, returns a string of text to be displayed to the user communicating the contents of that record
   */
  generateHistoryRecordText(record: any) {
    const historyRecord = generateHistoryRecordText(record).split('::');

    return {
      date: historyRecord[0],
      text: historyRecord[1],
    };
  }

  /**
   * triggered by the "pencil/edit" icon; sets the item to be in edit mode
   */
  editItemButtonAction($event: any): void {
    this.treeItemService.setTreeEvent({
      type: TreeEventType.EditItem,
      data: structuredClone(this.node().data),
    });
  }

  showHideFullHistory(itemId: string): void {
    const data = {
      itemId,
      isExpanded: this.isExpanded(),
      isHistoryExpanded: !this.isHistoryExpanded(),
    };
    this.expandItemDetails.emit(data);
  }

  /**
   * returns a boolean indicating whether this are additional events in this item's history (aside from those in "recent history") that are currently not displayed
   */
  additionalHistoryHidden(): boolean {
    return this.node().data.history.length > APP_CONFIG.NO_OF_EVENTS_IN_RECENT_HISTORY;
  }

  expandCurrentItem(itemId: string) {
    const data = {
      itemId,
      isExpanded: !this.isExpanded(),
      isHistoryExpanded: this.isHistoryExpanded(),
    };
    this.expandItemDetails.emit(data);
  }
}
